import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import axios from 'src/axios';
import { APICONSTANTS } from 'src/components/Constants';
import { UserContext } from 'src/store/ContextStore';
import {
  getState as getConnectivityState,
  subscribe as subscribeConnectivity
} from 'src/utils/connectivityDetector';

// Stable synthetic id so we never collide with a server-generated ObjectId.
const CONNECTIVITY_SYNTHETIC_ID = '__connectivity__';

function buildConnectivityRow() {
  return {
    _id: CONNECTIVITY_SYNTHETIC_ID,
    source: 'connectivity',
    severity: 'error',
    title: '',
    body: 'Connection issue detected. Trying to reconnect…',
    dismissible: false
  };
}

// Active-poll interval. 30s matches the spec; tabs in the background
// still tick at this cadence — the `/active` endpoint sets
// Cache-Control: max-age=10 so the actual request rate is low.
const POLL_INTERVAL_MS = 30_000;

export const AnnouncementsContext = createContext({
  announcements: [],
  dismiss: () => {}
});

export function AnnouncementsProvider({ children }) {
  const { user } = useContext(UserContext) || {};
  const isAuthed = !!user?._id;

  const [serverAnnouncements, setServerAnnouncements] = useState([]);
  const [dismissedLocally, setDismissedLocally] = useState(new Set());
  const [connectivity, setConnectivity] = useState(() =>
    getConnectivityState()
  );

  useEffect(() => {
    const unsub = subscribeConnectivity(setConnectivity);
    return unsub;
  }, []);

  const fetchRef = useRef(0);

  const fetchActive = useCallback(async () => {
    if (!isAuthed) return;
    const seq = ++fetchRef.current;
    try {
      const res = await axios.get(APICONSTANTS.announcementsActive);
      if (seq !== fetchRef.current) return; // stale response
      setServerAnnouncements(Array.isArray(res?.data) ? res.data : []);
    } catch (err) {
      // Silently swallow — banner just won't refresh this tick. The
      // connectivity detector will surface a synthetic banner in PR-B.
      // eslint-disable-next-line no-console
      console.debug('[announcements] fetchActive failed:', err?.message);
    }
  }, [isAuthed]);

  useEffect(() => {
    if (!isAuthed) {
      setServerAnnouncements([]);
      setDismissedLocally(new Set());
      return undefined;
    }
    fetchActive();
    const id = setInterval(fetchActive, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [isAuthed, fetchActive]);

  const dismiss = useCallback(async (announcementId) => {
    if (!announcementId) return;
    // Optimistic local hide so the row vanishes immediately even if
    // the network round-trip is slow.
    setDismissedLocally((prev) => {
      if (prev.has(announcementId)) return prev;
      const next = new Set(prev);
      next.add(announcementId);
      return next;
    });
    try {
      await axios.post(APICONSTANTS.announcementDismiss(announcementId));
    } catch (err) {
      // Roll back the optimistic hide if the server refused (e.g.
      // 409 non-dismissible). The next poll will re-introduce it.
      // eslint-disable-next-line no-console
      console.warn('[announcements] dismiss failed:', err?.message);
      setDismissedLocally((prev) => {
        const next = new Set(prev);
        next.delete(announcementId);
        return next;
      });
    }
  }, []);

  const value = useMemo(() => {
    const visible = serverAnnouncements.filter(
      (a) => !dismissedLocally.has(a._id)
    );
    // Surface the synthetic connectivity banner above server rows so
    // the user sees it first. It's non-dismissible and clears
    // automatically when the next 2xx request lands.
    const merged = connectivity.isOffline
      ? [buildConnectivityRow(), ...visible]
      : visible;
    return {
      announcements: merged,
      dismiss,
      refresh: fetchActive
    };
  }, [
    serverAnnouncements,
    dismissedLocally,
    dismiss,
    fetchActive,
    connectivity.isOffline
  ]);

  return (
    <AnnouncementsContext.Provider value={value}>
      {children}
    </AnnouncementsContext.Provider>
  );
}

export function useAnnouncements() {
  return useContext(AnnouncementsContext);
}
